import {
  JSNS,
  type Content,
  type JsonObject,
  type LocaleInfo,
  type PrefBag,
  type PrefEntry,
  type PrefWriteBag,
  type ZimbraPrefKey,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';

/** GetPrefsResponse: `{ pref: [{ name, modified, _content }] }`. */
interface GetPrefsResponseBody {
  pref?: PrefEntry[] | PrefEntry;
  [k: string]: unknown;
}

/** GetWhiteBlackListResponse. */
export interface WhiteBlackList {
  white: string[];
  black: string[];
}

/**
 * Out-of-office / vacation settings — a typed slice of the `zimbraPref*` OOO
 * attrs (§13). Dates are Zimbra's `yyyyMMddHHmmssZ` strings; empty = indefinite.
 */
export interface VacationSettings {
  /** master on/off — zimbraPrefOutOfOfficeReplyEnabled */
  enabled?: boolean;
  /** internal reply text — zimbraPrefOutOfOfficeReply */
  reply?: string;
  /** window start — zimbraPrefOutOfOfficeFromDate */
  fromDate?: string;
  /** window end — zimbraPrefOutOfOfficeUntilDate */
  untilDate?: string;
  /** external reply on/off — zimbraPrefOutOfOfficeExternalReplyEnabled */
  externalReplyEnabled?: boolean;
  /** external reply text — zimbraPrefOutOfOfficeExternalReply */
  externalReply?: string;
  /** don't reply to strangers — zimbraPrefOutOfOfficeSuppressExternalReply */
  suppressExternalReply?: boolean;
  /** free/busy status while away — zimbraPrefOutOfOfficeFreeBusyStatus */
  freeBusyStatus?: string;
  /** warn on login it's on — zimbraPrefOutOfOfficeStatusAlertOnLogin */
  alertOnLogin?: boolean;
}

function asArray<T>(v: T[] | T | undefined): T[] {
  if (v === undefined) return [];
  return Array.isArray(v) ? v : [v];
}

function content(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return (value[0] as Content | undefined)?._content;
  if (value && typeof value === 'object' && '_content' in value) {
    return String((value as Content)._content);
  }
  return undefined;
}

/**
 * Build the repeating `pref` payload for ModifyPrefs/GetPrefs from a flat bag.
 * Multi-valued prefs repeat the same `name`; `null` emits an empty value to
 * clear the pref.
 */
function prefsFromBag(bag: PrefWriteBag): Array<{ name: string; _content: string }> {
  const out: Array<{ name: string; _content: string }> = [];
  for (const [name, value] of Object.entries(bag)) {
    if (value === undefined) continue;
    if (value === null) {
      out.push({ name, _content: '' });
    } else if (Array.isArray(value)) {
      for (const v of value) out.push({ name, _content: v });
    } else {
      out.push({ name, _content: value });
    }
  }
  return out;
}

function boolStr(b: boolean): string {
  return b ? 'TRUE' : 'FALSE';
}

function strBool(v: string | string[] | undefined): boolean | undefined {
  const s = Array.isArray(v) ? v[0] : v;
  if (s === undefined) return undefined;
  return s === 'TRUE' || s === 'true' || s === '1';
}

/**
 * Preferences domain (urn:zimbraAccount): the `zimbraPref*` surface plus
 * locales, skins, the white/black sender lists, and zimlet enablement.
 *
 * One public method per SOAP command; see docs/features/account-prefs-security.md.
 */
export class PrefsApi {
  constructor(private readonly client: ZimbraClient) {}

  /* ---------------------------------------------------------------------- */
  /* GetPrefs / ModifyPrefs                                                  */
  /* ---------------------------------------------------------------------- */

  /**
   * GetPrefsRequest — read preferences. Pass `names` to limit; omit for all.
   * Returns a flat `_attrs`-style bag: single value as a string, repeated
   * `name`s collapsed into a string[]. (The raw `pref` array is also exposed via
   * {@link getRaw} if last-modified timestamps are needed.)
   */
  async get(names?: ZimbraPrefKey[] | string[]): Promise<PrefBag> {
    const body = await this.getRaw(names);
    const bag: PrefBag = {};
    for (const p of asArray(body.pref)) {
      if (!p.name) continue;
      const value = p._content ?? '';
      const existing = bag[p.name];
      if (existing === undefined) {
        bag[p.name] = value;
      } else if (Array.isArray(existing)) {
        existing.push(value);
      } else {
        bag[p.name] = [existing, value];
      }
    }
    return bag;
  }

  /** GetPrefsRequest — raw response body (keeps `modified` timestamps + dupes). */
  async getRaw(names?: ZimbraPrefKey[] | string[]): Promise<GetPrefsResponseBody> {
    const params: JsonObject = {};
    if (names && names.length > 0) {
      params.pref = names.map((name) => ({ name }));
    }
    return this.client.call<GetPrefsResponseBody>(JSNS.account, 'GetPrefs', params);
  }

  /**
   * ModifyPrefsRequest — write preferences.
   *
   * SEMANTICS: this is a PER-PREF set, not a full-bag replace. Only the prefs
   * present in `changes` are written; everything else is untouched. For a
   * multi-valued pref, pass a string[] (the whole new value-set — the server
   * replaces all values for that name). Pass `null` to CLEAR a pref (emits an
   * empty value). The typical UI flow is: diff against the last-loaded bag and
   * send only the changed keys in one ModifyPrefs.
   */
  async modify(changes: PrefWriteBag): Promise<JsonObject> {
    return this.client.call(JSNS.account, 'ModifyPrefs', { pref: prefsFromBag(changes) });
  }

  /* ---------------------------------------------------------------------- */
  /* Out-of-office / vacation (typed slice of ModifyPrefs)                   */
  /* ---------------------------------------------------------------------- */

  /** Read the out-of-office settings via GetPrefs over the OOO `zimbraPref*` attrs. */
  async getVacation(): Promise<VacationSettings> {
    const keys: ZimbraPrefKey[] = [
      'zimbraPrefOutOfOfficeReplyEnabled',
      'zimbraPrefOutOfOfficeReply',
      'zimbraPrefOutOfOfficeFromDate',
      'zimbraPrefOutOfOfficeUntilDate',
      'zimbraPrefOutOfOfficeExternalReplyEnabled',
      'zimbraPrefOutOfOfficeExternalReply',
      'zimbraPrefOutOfOfficeSuppressExternalReply',
      'zimbraPrefOutOfOfficeFreeBusyStatus',
      'zimbraPrefOutOfOfficeStatusAlertOnLogin',
    ];
    const bag = await this.get(keys);
    const out: VacationSettings = {};
    const enabled = strBool(bag.zimbraPrefOutOfOfficeReplyEnabled);
    if (enabled !== undefined) out.enabled = enabled;
    const reply = bag.zimbraPrefOutOfOfficeReply;
    if (reply !== undefined) out.reply = Array.isArray(reply) ? reply[0] : reply;
    const from = bag.zimbraPrefOutOfOfficeFromDate;
    if (from !== undefined) out.fromDate = Array.isArray(from) ? from[0] : from;
    const until = bag.zimbraPrefOutOfOfficeUntilDate;
    if (until !== undefined) out.untilDate = Array.isArray(until) ? until[0] : until;
    const extEnabled = strBool(bag.zimbraPrefOutOfOfficeExternalReplyEnabled);
    if (extEnabled !== undefined) out.externalReplyEnabled = extEnabled;
    const ext = bag.zimbraPrefOutOfOfficeExternalReply;
    if (ext !== undefined) out.externalReply = Array.isArray(ext) ? ext[0] : ext;
    const suppress = strBool(bag.zimbraPrefOutOfOfficeSuppressExternalReply);
    if (suppress !== undefined) out.suppressExternalReply = suppress;
    const fb = bag.zimbraPrefOutOfOfficeFreeBusyStatus;
    if (fb !== undefined) out.freeBusyStatus = Array.isArray(fb) ? fb[0] : fb;
    const alert = strBool(bag.zimbraPrefOutOfOfficeStatusAlertOnLogin);
    if (alert !== undefined) out.alertOnLogin = alert;
    return out;
  }

  /** Write the out-of-office settings via ModifyPrefs (only the provided fields). */
  async setVacation(settings: VacationSettings): Promise<JsonObject> {
    const changes: PrefWriteBag = {};
    if (settings.enabled !== undefined) {
      changes.zimbraPrefOutOfOfficeReplyEnabled = boolStr(settings.enabled);
    }
    if (settings.reply !== undefined) changes.zimbraPrefOutOfOfficeReply = settings.reply;
    if (settings.fromDate !== undefined) changes.zimbraPrefOutOfOfficeFromDate = settings.fromDate;
    if (settings.untilDate !== undefined) {
      changes.zimbraPrefOutOfOfficeUntilDate = settings.untilDate;
    }
    if (settings.externalReplyEnabled !== undefined) {
      changes.zimbraPrefOutOfOfficeExternalReplyEnabled = boolStr(settings.externalReplyEnabled);
    }
    if (settings.externalReply !== undefined) {
      changes.zimbraPrefOutOfOfficeExternalReply = settings.externalReply;
    }
    if (settings.suppressExternalReply !== undefined) {
      changes.zimbraPrefOutOfOfficeSuppressExternalReply = boolStr(settings.suppressExternalReply);
    }
    if (settings.freeBusyStatus !== undefined) {
      changes.zimbraPrefOutOfOfficeFreeBusyStatus = settings.freeBusyStatus;
    }
    if (settings.alertOnLogin !== undefined) {
      changes.zimbraPrefOutOfOfficeStatusAlertOnLogin = boolStr(settings.alertOnLogin);
    }
    return this.modify(changes);
  }

  /* ---------------------------------------------------------------------- */
  /* Locales / skins                                                         */
  /* ---------------------------------------------------------------------- */

  /** GetAvailableLocalesRequest — locales installed on this server. */
  async getAvailableLocales(): Promise<LocaleInfo[]> {
    const raw = await this.client.call<{ locale?: LocaleInfo[] | LocaleInfo }>(
      JSNS.account,
      'GetAvailableLocales',
      {},
    );
    return asArray(raw.locale);
  }

  /** GetAllLocalesRequest — the full known locale set. */
  async getAllLocales(): Promise<LocaleInfo[]> {
    const raw = await this.client.call<{ locale?: LocaleInfo[] | LocaleInfo }>(
      JSNS.account,
      'GetAllLocales',
      {},
    );
    return asArray(raw.locale);
  }

  /** GetAvailableSkinsRequest — installed UI skins (legacy theme picker). */
  async getAvailableSkins(): Promise<string[]> {
    type SkinEntry = { name?: string; _content?: string };
    const raw = await this.client.call<{ skin?: SkinEntry[] | SkinEntry }>(
      JSNS.account,
      'GetAvailableSkins',
      {},
    );
    return asArray(raw.skin)
      .map((s) => s.name ?? s._content)
      .filter((s): s is string => typeof s === 'string');
  }

  /* ---------------------------------------------------------------------- */
  /* White / black sender lists                                              */
  /* ---------------------------------------------------------------------- */

  /** GetWhiteBlackListRequest — the per-user trusted/blocked sender lists. */
  async getWhiteBlackList(): Promise<WhiteBlackList> {
    const raw = await this.client.call<{
      whiteList?: { addr?: Array<Content | string> | (Content | string) };
      blackList?: { addr?: Array<Content | string> | (Content | string) };
    }>(JSNS.account, 'GetWhiteBlackList', {});
    const toList = (addr: Array<Content | string> | (Content | string) | undefined): string[] =>
      asArray(addr)
        .map((a) => (typeof a === 'string' ? a : a._content))
        .filter((s): s is string => typeof s === 'string');
    return { white: toList(raw.whiteList?.addr), black: toList(raw.blackList?.addr) };
  }

  /**
   * ModifyWhiteBlackListRequest — add/remove trusted/blocked senders.
   *
   * Each `<addr op="+|-">` adds (`+`) or removes (`-`) an entry. The `op`
   * semantics are documented Zimbra behavior, not binary-verified (doc Open #1).
   * Pass full lists with explicit ops; omit a list to leave it untouched.
   */
  async modifyWhiteBlackList(changes: {
    white?: Array<{ addr: string; op?: '+' | '-' }>;
    black?: Array<{ addr: string; op?: '+' | '-' }>;
  }): Promise<JsonObject> {
    const params: JsonObject = {};
    const mk = (entries: Array<{ addr: string; op?: '+' | '-' }>) =>
      entries.map((e) => ({ _content: e.addr, ...(e.op ? { op: e.op } : {}) }));
    if (changes.white) params.whiteList = { addr: mk(changes.white) };
    if (changes.black) params.blackList = { addr: mk(changes.black) };
    return this.client.call(JSNS.account, 'ModifyWhiteBlackList', params);
  }

  /* ---------------------------------------------------------------------- */
  /* Zimlet preferences                                                      */
  /* ---------------------------------------------------------------------- */

  /**
   * ModifyZimletPrefsRequest — enable/disable user-toggleable zimlets.
   *
   * Emits `<zimlet name presence="enabled|disabled"/>`. The `presence` attr name
   * and its value vocabulary are ASSUMED — verify vs live capture (doc Open #1).
   */
  async modifyZimletPrefs(changes: Array<{ name: string; enabled: boolean }>): Promise<JsonObject> {
    return this.client.call(JSNS.account, 'ModifyZimletPrefs', {
      // ASSUMED presence attr name + enum values — verify vs live capture
      zimlet: changes.map((c) => ({ name: c.name, presence: c.enabled ? 'enabled' : 'disabled' })),
    });
  }
}
